import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CanonicalInterval,
  PaymentTabs,
  PlanFamily,
  PlanKind,
} from 'src/types';
import { RootState } from '../index';
import {
  DEFAULT_BUY_ANNUALLY_PLAN_KIND_MAP,
  DEFAULT_UPDATE_ANNUALLY_PLAN_KIND_MAP,
  DEFAULT_UPDATE_MONTHLY_PLAN_KIND_MAP,
} from 'src/constants';
import { getCorrespondingPlanKind } from 'src/utils';
import { walletApi } from 'src/store/services';

interface PaymentControlState {
  selectedPeriod: CanonicalInterval;
  selectedFamily?: PlanFamily;
  selectedPlanSetByFamily: Record<PlanFamily, PlanKind>;
  initialized: boolean;
  selectedPaymentTab: PaymentTabs;
  selectedPlan?: PlanKind;
}

const initialState: PaymentControlState = {
  selectedPeriod: CanonicalInterval.ANNUALLY,
  selectedFamily: undefined,
  selectedPlanSetByFamily: {
    ...DEFAULT_BUY_ANNUALLY_PLAN_KIND_MAP,
    [PlanFamily.UNDEFINED]: PlanKind.STANDARD_ANNUALLY, // it's not used
    [PlanFamily.CREDIT]: PlanKind.CREDIT_1_MONTHLY,
  },
  initialized: false,
  selectedPaymentTab: PaymentTabs.INDIVIDUAL,
  selectedPlan: undefined,
};

export const paymentControlSlice = createSlice({
  name: 'paymentControl',
  initialState,
  reducers: {
    setSelectedPeriod: (
      state,
      { payload: newPeriod }: PayloadAction<CanonicalInterval>,
    ) => {
      state.selectedPeriod = newPeriod;

      Object.entries(state.selectedPlanSetByFamily).forEach(
        ([planFamily, currentPlan]) => {
          if (planFamily === PlanFamily.UNDEFINED) {
            return;
          }

          state.selectedPlanSetByFamily[planFamily as PlanFamily] =
            getCorrespondingPlanKind({
              currentPlan,
              planFamily: planFamily as PlanFamily,
              targetInterval: newPeriod,
            });
        },
      );

      if (!!state.selectedPlan) {
        // todo change plan kind to the same with another period
        // state.selectedPlan =
      }
    },
    setSelectedFamily: (
      state,
      action: PayloadAction<PlanFamily | undefined>,
    ) => {
      state.selectedFamily = action.payload;
    },
    setSelectedPlanByFamily: (
      state,
      action: PayloadAction<{ planFamily: PlanFamily; planKind: PlanKind }>,
    ) => {
      const { planFamily, planKind } = action.payload;

      state.selectedPlanSetByFamily[planFamily] = planKind;
    },
    setSelectedPlan: (
      state,
      action: PayloadAction<{ planKind?: PlanKind }>,
    ) => {
      const { planKind } = action.payload;

      state.selectedPlan = planKind;
    },
    setComplexPaymentPlan: (
      state,
      action: PayloadAction<{
        planFamily: PlanFamily;
        planKind: PlanKind;
        period: CanonicalInterval;
      }>,
    ) => {
      const { planFamily, planKind, period } = action.payload;

      state.selectedFamily = planFamily;
      state.selectedPlanSetByFamily[planFamily] = planKind;
      state.selectedPeriod = period;
      state.selectedPaymentTab =
        planFamily === PlanFamily.BUSINESS
          ? PaymentTabs.BUSINESS
          : PaymentTabs.INDIVIDUAL;
    },

    setSelectedPaymentTab: (state, action: PayloadAction<PaymentTabs>) => {
      state.selectedPaymentTab = action.payload;
    },

    resetPaymentControl: () => initialState,
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      walletApi.endpoints.getUserSubscriptionInfo.matchFulfilled,
      (state, { payload }) => {
        if (
          state.initialized ||
          (payload.interval !== CanonicalInterval.MONTHLY &&
            payload.interval !== CanonicalInterval.ANNUALLY)
        ) {
          return;
        }

        state.selectedPeriod = !!payload.interval
          ? payload.interval
          : CanonicalInterval.ANNUALLY;

        if (payload.interval === CanonicalInterval.MONTHLY) {
          state.selectedPlanSetByFamily = {
            ...DEFAULT_UPDATE_MONTHLY_PLAN_KIND_MAP,
            [PlanFamily.UNDEFINED]: PlanKind.STANDARD_MONTHLY,
            [PlanFamily.CREDIT]: PlanKind.CREDIT_1_MONTHLY,
          };
        }

        if (payload.interval === CanonicalInterval.ANNUALLY) {
          state.selectedPlanSetByFamily = {
            ...DEFAULT_UPDATE_ANNUALLY_PLAN_KIND_MAP,
            [PlanFamily.UNDEFINED]: PlanKind.STANDARD_ANNUALLY,
            [PlanFamily.CREDIT]: PlanKind.CREDIT_1_MONTHLY,
          };
        }

        state.initialized = true;
      },
    );
  },
});

export const paymentControlState = (state: RootState) =>
  state.paymentControl as PaymentControlState;

export const {
  setSelectedPeriod,
  setSelectedFamily,
  setSelectedPlanByFamily,
  setComplexPaymentPlan,
  setSelectedPaymentTab,
  resetPaymentControl,
  setSelectedPlan,
} = paymentControlSlice.actions;

export default paymentControlSlice.reducer;
